import CalendarIcon from '@/components/icons/CalendarIcon';
import InvoiceIcon from '@/components/icons/InvoiceIcon';
import PackageIcon from '@/components/icons/PackageIcon';
import { Button } from '@/components/ui/button';
import { useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import DashboardEvents from '@/features/dashboard/components/DashboardEvents/DashboardEvents';
import DashboardTransactions from '@/features/dashboard/components/DashboardTransactions/DashboardTransactions';
import DashboardOrders from '@/features/dashboard/components/DashboardOrders/DashboardOrders';
import { t } from 'i18next';
import { usePermissionsListApi } from '@/features/settings/hooks/usePermissionsListApi';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { OwnerPermissionsResponseDto } from '@/features/owners/types/owners-dto';
import { Roles } from '@/@types/auth.types';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';

enum ActivityButton {
	Events,
	Transactions,
	Orders,
}

export const DashboardRecentActivities = () => {
	const [activeActivity, setActiveActivity] = useState<ActivityButton | null>(
		null,
	);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const userRole = useUserRole();
	const { permissionsListData: permissions } = usePermissionsListApi({
		role: userRole,
	});
	const { ordersEnabled } = useMultiCurrencyStorage();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'center',
		dragFree: true,
	});

	const clickHandler = (index: number) => {
		setActiveActivity(index);
		if (emblaApi) {
			emblaApi.scrollTo(index);
		}
	};

	const activitiesButtonsList = useMemo(
		() => [
			{
				type: ActivityButton.Events,
				name: 'main.dashboard.components.recentActivities.list.events.title',
				icon: CalendarIcon,
				enabled: (permissions?: OwnerPermissionsResponseDto) =>
					userRole === Roles.admin ? true : permissions?.showCalendar,
				component: () => <DashboardEvents isMobile={isMobile} />,
			},
			{
				type: ActivityButton.Transactions,
				name: 'main.dashboard.components.recentActivities.list.transactions.title',
				icon: InvoiceIcon,
				enabled: (permissions?: OwnerPermissionsResponseDto) =>
					userRole === Roles.admin ? true : permissions?.showFinances,
				component: () => <DashboardTransactions isMobile={isMobile} />,
			},
			{
				type: ActivityButton.Orders,
				name: 'main.dashboard.components.recentActivities.list.orders.title',
				icon: PackageIcon,
				enabled: () => ordersEnabled,
				component: () => <DashboardOrders isMobile={isMobile} />,
			},
		],
		[isMobile, userRole, ordersEnabled],
	);

	const visibleActivities = useMemo(() => {
		return activitiesButtonsList.filter((element) =>
			element.enabled(permissions),
		);
	}, [permissions, activitiesButtonsList]);

	useEffect(() => {
		if (visibleActivities.length > 0) {
			setActiveActivity(visibleActivities[0].type);
		}
	}, [visibleActivities]);

	return (
		<div
			className={cn(
				'p-6 flex-1 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10 select-none',
				isMobile && 'p-0 border-0 bg-popover-30',
			)}>
			<h4
				className={cn(
					'text-xl font-medium',
					isMobile && 'text-lg font-medium',
				)}>
				{t('main.dashboard.components.recentActivities.title')}
			</h4>
			<div className='flex-1 flex flex-col gap-6'>
				<div
					className={cn(
						'-mx-6 px-6 max-w-screen overflow-hidden',
						isMobile && '-mx-4 px-4',
					)}
					ref={emblaRef}>
					<div className='flex gap-2 touch-pan-y'>
						{visibleActivities.map(({ type, name, icon: Icon }) => (
							<Button
								key={type}
								variant={
									activeActivity === type
										? 'default'
										: 'outline'
								}
								className={cn(
									`hover:shadow-none ${
										activeActivity === type
											? ''
											: 'hover:bg-primary-color-50'
									} `,
									isMobile && 'h-9 px-3.5 py-2',
								)}
								onClick={() => clickHandler(type)}>
								<Icon
									className={`${
										activeActivity === type
											? 'stroke-popover-10'
											: 'stroke-popover-1000'
									}`}
								/>
								{t(name)}
							</Button>
						))}
					</div>
				</div>
				{visibleActivities
					.find((element) => element.type === activeActivity)
					?.component()}
			</div>
		</div>
	);
};
